// =============================================================================
// Resource Type Form Modal - Form per Tipi di Risorsa con Schema Builder
// =============================================================================

import { useEffect, useMemo } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Plus, Trash2, GripVertical, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useCreateResourceType, useUpdateResourceType } from '../../api/hooks';
import { ResourceType } from '../../types';
import {
  RESOURCE_TYPE_ICON_NAMES,
  resolveResourceTypeIcon,
} from '../../lib/resourceTypeIcons';
import { IconCombobox, type IconOption } from '../ui/IconCombobox';

// =============================================================================
// Schema Zod
// =============================================================================

const attributeFieldSchema = z.object({
  key: z.string().min(1, i18n.t('validation.keyRequired', { ns: 'settings' })),
  label: z.string().min(1, i18n.t('validation.labelRequired', { ns: 'settings' })),
  type: z.enum(['text', 'number', 'boolean', 'select', 'date', 'email', 'phone']),
  required: z.boolean(),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

const formSchema = z.object({
  name: z.string().min(1, i18n.t('validation.nameRequired', { ns: 'settings' })),
  code: z.string().min(1, i18n.t('validation.codeRequired', { ns: 'settings' })),
  description: z.string().optional(),
  color: z.string().optional(),
  icon: z.string().optional(),
  attributesSchema: z.array(attributeFieldSchema),
});

type FormData = z.infer<typeof formSchema>;

// =============================================================================
// Props
// =============================================================================

interface Props {
  resourceType: ResourceType | null;
  onClose: () => void;
}

// =============================================================================
// Costanti
// =============================================================================

const FIELD_TYPES = [
  { value: 'text', labelKey: 'fieldTypes.text' },
  { value: 'number', labelKey: 'fieldTypes.number' },
  { value: 'boolean', labelKey: 'fieldTypes.boolean' },
  { value: 'select', labelKey: 'fieldTypes.select' },
  { value: 'date', labelKey: 'fieldTypes.date' },
  { value: 'email', labelKey: 'fieldTypes.email' },
  { value: 'phone', labelKey: 'fieldTypes.phone' },
];

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#6B7280', // Gray
];

// =============================================================================
// Component
// =============================================================================

export function ResourceTypeFormModal({ resourceType, onClose }: Props) {
  const { t } = useTranslation('settings');
  const createResourceType = useCreateResourceType();
  const updateResourceType = useUpdateResourceType();
  
  const isEditing = !!resourceType;
  const isPending = createResourceType.isPending || updateResourceType.isPending;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      color: PRESET_COLORS[0],
      icon: '',
      attributesSchema: [],
    },
  });

  // Field array per gli attributi
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'attributesSchema',
  });

  const watchedColor = watch('color');
  const watchedIcon = watch('icon');
  const HeaderIcon = resolveResourceTypeIcon(watchedIcon) ?? Layers;

  // Prima opzione = nessuna icona (nel quadratino resta l'iniziale del nome).
  const iconOptions = useMemo<IconOption[]>(
    () => [
      { name: '', label: t('resourceTypes.iconNone'), Icon: null },
      ...RESOURCE_TYPE_ICON_NAMES.map((name) => ({
        name,
        label: t(`resourceTypes.iconNames.${name}`),
        Icon: resolveResourceTypeIcon(name),
      })),
    ],
    [t],
  );

  // Carica dati esistenti
  useEffect(() => {
    if (resourceType) {
      setValue('name', resourceType.name);
      setValue('code', resourceType.code);
      setValue('description', resourceType.description || '');
      setValue('color', resourceType.color || PRESET_COLORS[0]);
      setValue('icon', resourceType.icon || '');
      
      // Carica schema attributi
      if (resourceType.attributesSchema && Array.isArray(resourceType.attributesSchema)) {
        const attrs = resourceType.attributesSchema.map(a => ({
          ...a,
          required: a.required ?? false,
        }));
        setValue('attributesSchema', attrs);
      }
    }
  }, [resourceType, setValue]);

  // Auto-genera codice dal nome
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (!isEditing) {
      const code = name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      setValue('code', code);
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        name: data.name,
        code: data.code,
        description: data.description || undefined,
        color: data.color || undefined,
        // null (non undefined): l'update ignora i campi undefined, quindi
        // servirebbe altrimenti impossibile tornare all'iniziale testuale.
        icon: data.icon || null,
        attributesSchema: data.attributesSchema || [],
      };

      if (isEditing && resourceType) {
        await updateResourceType.mutateAsync({ 
          id: resourceType.id, 
          ...payload,
        } as any);
      } else {
        await createResourceType.mutateAsync(payload as any);
      }
      
      onClose();
    } catch (error) {
      console.error('Errore salvataggio:', error);
    }
  };

  const addAttribute = () => {
    const newIndex = fields.length;
    append({
      key: `field_${newIndex + 1}`,
      label: '',
      type: 'text',
      required: false,
      placeholder: '',
      options: [],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
              style={{ backgroundColor: watchedColor || PRESET_COLORS[0] }}
            >
              <HeaderIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? t('resourceTypes.editTitle') : t('resourceTypes.createTitle')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('resourceTypes.formSubtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:buttons.close')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Info Base */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common:fields.name')} *
                </label>
                <input
                  type="text"
                  {...register('name', { onChange: handleNameChange })}
                  className="w-full px-3 py-2 input"
                  placeholder={t('resourceTypes.namePlaceholder')}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('common:fields.code')} *
                </label>
                <input
                  type="text"
                  {...register('code')}
                  className="w-full px-3 py-2 input font-mono uppercase"
                  placeholder={t('resourceTypes.codePlaceholder')}
                  readOnly={isEditing}
                />
                {errors.code && (
                  <p className="text-red-500 text-xs mt-1">{errors.code.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common:fields.description')}
              </label>
              <textarea
                {...register('description')}
                rows={2}
                className="w-full px-3 py-2 input"
                placeholder={t('validation.descriptionOptionalPlaceholder')}
              />
            </div>

            {/* Colore */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common:fields.color')}
              </label>
              <div className="flex items-center gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setValue('color', color)}
                    className={`w-8 h-8 rounded-lg transition-transform ${
                      watchedColor === color ? 'ring-2 ring-offset-2 ring-emerald-500 scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
                <input
                  type="color"
                  {...register('color')}
                  className="w-8 h-8 rounded cursor-pointer"
                />
              </div>
            </div>

            {/* Icona */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('resourceTypes.iconLabel')}
              </label>
              <IconCombobox
                options={iconOptions}
                value={watchedIcon || ''}
                onChange={(name) => setValue('icon', name)}
                color={watchedColor || PRESET_COLORS[0]}
                placeholder={t('resourceTypes.iconPlaceholder')}
                noMatchLabel={t('resourceTypes.iconNoMatch')}
                className="max-w-xs"
              />
            </div>

            {/* Schema Builder */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-md font-semibold text-gray-900">{t('resourceTypes.customAttributesSection')}</h3>
                  <p className="text-sm text-gray-500">
                    {t('resourceTypes.customAttributesHint')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addAttribute}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg"
                >
                  <Plus className="w-4 h-4" />
                  {t('resourceTypes.addField')}
                </button>
              </div>

              {fields.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-200">
                  <p className="text-gray-500 mb-3">{t('resourceTypes.noAttributes')}</p>
                  <button
                    type="button"
                    onClick={addAttribute}
                    className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                  >
                    {t('resourceTypes.addFirstField')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <AttributeFieldRow
                      key={field.id}
                      index={index}
                      control={control}
                      register={register}
                      errors={errors}
                      watch={watch}
                      onRemove={() => remove(index)}
                      onMoveUp={() => index > 0 && move(index, index - 1)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="btn-primary px-6 py-2 flex items-center gap-2"
            >
              {isPending && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {isEditing ? t('common:buttons.saveChanges') : t('resourceTypes.createButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Attribute Field Row Component
// =============================================================================

interface AttributeFieldRowProps {
  index: number;
  control: any;
  register: any;
  errors: any;
  watch: any;
  onRemove: () => void;
  onMoveUp: () => void;
}

function AttributeFieldRow({
  index,
  control,
  register,
  errors,
  watch,
  onRemove,
  onMoveUp,
}: AttributeFieldRowProps) {
  const { t } = useTranslation('settings');
  const fieldType = watch(`attributesSchema.${index}.type`);
  const fieldErrors = errors.attributesSchema?.[index];

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div className="flex flex-col gap-1 pt-2">
          <button
            type="button"
            onClick={onMoveUp}
            className="p-1 text-gray-400 hover:text-gray-600"
            title={t('resourceTypes.moveUp')}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </div>

        {/* Fields */}
        <div className="flex-1 grid grid-cols-12 gap-3">
          {/* Label */}
          <div className="col-span-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('resourceTypes.fieldLabel')}</label>
            <input
              type="text"
              {...register(`attributesSchema.${index}.label`)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
              placeholder={t('validation.fieldLabelPlaceholder')}
            />
            {fieldErrors?.label && (
              <p className="text-red-500 text-xs mt-0.5">{fieldErrors.label.message}</p>
            )}
          </div>

          {/* Key */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('resourceTypes.fieldKey')}</label>
            <input
              type="text"
              {...register(`attributesSchema.${index}.key`)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded font-mono focus:ring-1 focus:ring-emerald-500"
              placeholder="specialization"
            />
            {fieldErrors?.key && (
              <p className="text-red-500 text-xs mt-0.5">{fieldErrors.key.message}</p>
            )}
          </div>

          {/* Type */}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('resourceTypes.fieldType')}</label>
            <Controller
              name={`attributesSchema.${index}.type`}
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
                >
                  {FIELD_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>
                      {t(ft.labelKey)}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>

          {/* Placeholder */}
          <div className="col-span-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('resourceTypes.fieldPlaceholder')}</label>
            <input
              type="text"
              {...register(`attributesSchema.${index}.placeholder`)}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
              placeholder={t('validation.helpTextPlaceholder')}
            />
          </div>

          {/* Required */}
          <div className="col-span-2 flex items-end pb-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register(`attributesSchema.${index}.required`)}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">{t('resourceTypes.fieldRequired')}</span>
            </label>
          </div>
        </div>

        {/* Delete */}
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
          title={t('resourceTypes.removeField')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Options for Select type */}
      {fieldType === 'select' && (
        <div className="mt-3 ml-8">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {t('resourceTypes.fieldOptions')}
          </label>
          <Controller
            name={`attributesSchema.${index}.options`}
            control={control}
            render={({ field }) => (
              <input
                type="text"
                value={field.value?.join(', ') || ''}
                onChange={(e) => {
                  const options = e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                  field.onChange(options);
                }}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-emerald-500"
                placeholder={t('resourceTypes.fieldOptionsPlaceholder')}
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
